import { Permission } from 'components/permissions/Permission';
import { PermissionEnum, PermissionNames } from 'components/permissions/PermissionEnums';
import { TransformedAccount, useCurrentAccount } from 'customHooks/useCurrentAccount';

const usePermissions = (allowed: Permission[] | PermissionEnum[] | PermissionEnum = []) => {
    const currentAccount = useCurrentAccount();
    const isPermitted = getIsPermitted(currentAccount, allowed);

    return { isPermitted, getIsPermitted: getIsPermitted.bind(null, currentAccount) };
};

const getIsPermitted = (
    account: TransformedAccount,
    requiredPermissions: Permission[] | PermissionEnum[] | PermissionEnum = [],
) => {
    if (!Array.isArray(requiredPermissions)) {
        requiredPermissions = [requiredPermissions];
    }

    if (requiredPermissions.length === 0) {
        return true;
    }
    const grantedPermissions = getGrantedPermissions(account);
    return requiredPermissions.some(requiredPermission =>
        grantedPermissions.some(grantedPermission => grantedPermission === requiredPermission),
    );
};

const getRole = (account: TransformedAccount) => {
    const roles = new Map();

    if (account.access.role === Permission.Editor) {
        roles.set(Permission.Editor, true);
    }

    if (account.access.role === Permission.Admin) {
        roles.set(Permission.Editor, true);
        roles.set(Permission.Admin, true);
    }

    return Array.from(roles.keys());
};

const getGrantedPermissions = (account: TransformedAccount) => {
    const legacyPermissions = getRole(account);

    return [...legacyPermissions, ...(account.access.permissions ?? [])];
};

const getPermissionRequiredMessage = (
    permissions: PermissionEnum | PermissionEnum[] | undefined,
    permissionAction: string | undefined,
) => {
    permissionAction ??= 'view this';

    if (permissions == undefined) {
        return `You do not have permission to ${permissionAction}.`;
    }

    return `You need the ${getPermissionNameList(permissions)} permission to ${permissionAction}.`;
};

const getPermissionNameList = (permissions: PermissionEnum | PermissionEnum[]) => {
    if (!Array.isArray(permissions)) {
        return getPermissionName(permissions);
    }

    let namedPermission = permissions.map(value => {
        return getPermissionName(value);
    });

    let lastPermission = namedPermission.pop();

    return namedPermission.join(', ') + ' or ' + lastPermission;
};

const getPermissionName = (permission: PermissionEnum) => {
    return "'" + (PermissionNames[permission] ?? permission) + "'";
};

export { usePermissions, getPermissionRequiredMessage, getPermissionName, getPermissionNameList };
